import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Upload, X, Navigation, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import { CATEGORY_META, STATUS_META } from "@/lib/communityMeta";

const CATS = Object.entries(CATEGORY_META);

export default function ReportIssue() {
  const { user } = useAuth();
  const nav = useNavigate();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const allowed = user && ["farmer", "day_worker"].includes(user.role);

  const [step, setStep] = useState(1); // 1=category, 2=details, 3=location+evidence, 4=review
  const [form, setForm] = useState({
    title: "", category: "water", description: "",
    village: user?.village || "", district: user?.district || "", state: user?.state || "",
    landmark: "", lat: null, lng: null, event_date: new Date().toISOString().slice(0, 10),
  });
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [similar, setSimilar] = useState([]);
  const [busy, setBusy] = useState(false);

  if (!allowed) {
    return (
      <AppShell>
        <div className="px-4 py-24 text-center">
          <h1 className="text-2xl font-bold">Not available</h1>
          <p className="text-gray-600 mt-2">Switch to Farmer or Day Worker workspace to report issues.</p>
        </div>
      </AppShell>
    );
  }

  const setF = (k) => (v) => setForm((p) => ({ ...p, [k]: v }));

  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error("Location not supported");
    toast.loading("Getting location…", { id: "loc" });
    navigator.geolocation.getCurrentPosition(
      (pos) => { setForm((p) => ({ ...p, lat: pos.coords.latitude, lng: pos.coords.longitude })); toast.success("Location set", { id: "loc" }); },
      (err) => toast.error(err.message || "Could not get location", { id: "loc" }),
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const uploadFiles = async (files, kind) => {
    const urls = [];
    for (const f of files) {
      if (f.size > 10 * 1024 * 1024) { toast.error(`${f.name} > 10MB — skipped`); continue; }
      const fd = new FormData();
      fd.append("file", f);
      try {
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url.startsWith("http") ? data.url : `${API.replace(/\/api$/, "")}${data.url}`);
      } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || `Upload failed: ${f.name}`); }
    }
    if (kind === "photo") setPhotos((p) => [...p, ...urls]);
    else setVideos((p) => [...p, ...urls]);
  };

  const checkDuplicates = async () => {
    try {
      const { data } = await api.post("/issues/check-duplicate", {
        category: form.category, village: form.village, title: form.title,
        description: form.description, lat: form.lat, lng: form.lng,
      });
      return data.matches || [];
    } catch { return []; }
  };

  const next = async () => {
    if (step === 1 && !form.category) return toast.error("Please pick a category");
    if (step === 2) {
      if (!form.title.trim() || form.title.trim().length < 4) return toast.error("Issue title must be at least 4 characters");
      if (!form.description.trim() || form.description.trim().length < 5) return toast.error("Please describe the issue");
    }
    if (step === 3) {
      if (!form.village.trim()) return toast.error("Please enter your village / area");
      // Run duplicate check before letting user hit Submit
      setBusy(true);
      const m = await checkDuplicates();
      setBusy(false);
      setSimilar(m);
      // Advance to review step regardless — review page shows the warning
    }
    setStep((s) => s + 1);
  };

  const submit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/issues", {
        ...form, photos, videos,
      });
      toast.success("Issue reported — thank you");
      nav(`/community/${data.id}`);
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.existing_issue) {
        toast.error(detail.message || "Similar issue already exists");
        nav(`/community/${detail.existing_issue.id}`);
      } else {
        toast.error(formatApiErrorDetail(detail) || "Could not submit issue");
      }
    } finally { setBusy(false); }
  };

  const joinExisting = async (issueId) => {
    try {
      await api.post(`/issues/${issueId}/affected`);
      toast.success("You have joined this issue");
      nav(`/community/${issueId}`);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-8 max-w-3xl mx-auto">
        <button onClick={() => (step > 1 ? setStep(step - 1) : nav(-1))}
          className="inline-flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900">
          <ArrowLeft className="h-4 w-4" /> Back
        </button>
        <div className="mt-4 flex items-center gap-2">
          {[1, 2, 3, 4].map((s) => (
            <div key={s} className={`h-1.5 flex-1 rounded-full ${s <= step ? theme.bg : "bg-gray-200"}`} />
          ))}
        </div>
        <div className="text-[11px] uppercase tracking-widest text-gray-500 mt-2">Step {step} of 4</div>
        <h1 className="text-3xl font-bold mt-1 text-gray-900">Report a local issue</h1>

        <div className="mt-6 rounded-3xl bg-white border border-gray-100 p-6 md:p-8 premium-shadow">
          {step === 1 && (
            <>
              <div className="font-semibold text-gray-900">Pick a category</div>
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 gap-3">
                {CATS.map(([k, v]) => (
                  <button key={k} data-testid={`issue-cat-${k}`} onClick={() => setF("category")(k)}
                    className={`p-4 rounded-2xl border text-left transition-all ${form.category === k ? `${theme.bg} text-white border-transparent` : "bg-white border-gray-200 hover:border-gray-300"}`}>
                    <div className="text-2xl">{v.icon}</div>
                    <div className="mt-2 text-sm font-semibold">{v.label}</div>
                  </button>
                ))}
              </div>
            </>
          )}
          {step === 2 && (
            <>
              <label className="text-xs font-semibold text-gray-700">Issue title *</label>
              <input data-testid="issue-title" value={form.title} onChange={(e) => setF("title")(e.target.value)}
                placeholder="e.g. Drinking water not coming for 3 days"
                className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
              <label className="text-xs font-semibold text-gray-700 mt-4 block">Describe the issue *</label>
              <textarea data-testid="issue-description" value={form.description}
                onChange={(e) => setF("description")(e.target.value)} rows={6}
                placeholder={"Water has not come for 3 days.\n20 families are affected.\nThe problem is near the main road.\nPlease check the issue."}
                className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 whitespace-pre-line" />
              <p className="mt-2 text-xs text-gray-500">Tip: use bullet points (•) or new lines for each detail. The post page will auto-format them.</p>
            </>
          )}
          {step === 3 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-semibold text-gray-700">Village / Area *</label>
                  <input data-testid="issue-village" value={form.village} onChange={(e) => setF("village")(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" placeholder="e.g. ABC Village" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">District</label>
                  <input value={form.district} onChange={(e) => setF("district")(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">State</label>
                  <input value={form.state} onChange={(e) => setF("state")(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-700">Landmark</label>
                  <input value={form.landmark} onChange={(e) => setF("landmark")(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3" placeholder="Near school, main road…" />
                </div>
              </div>
              <button type="button" onClick={useMyLocation}
                className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200 text-sm">
                <Navigation className="h-3.5 w-3.5" />{form.lat ? `📍 Location set (${form.lat.toFixed(3)}, ${form.lng.toFixed(3)})` : "Use my current location"}
              </button>
              <div className="mt-6 border-t border-gray-100 pt-6">
                <div className="text-xs font-semibold text-gray-700">Photos</div>
                <label className="mt-2 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gray-50 border border-gray-200 text-sm cursor-pointer">
                  <Upload className="h-3.5 w-3.5" /> Add photos
                  <input data-testid="issue-photos" type="file" accept="image/*" multiple className="hidden"
                    onChange={(e) => { uploadFiles(Array.from(e.target.files || []), "photo"); e.target.value = ""; }} />
                </label>
                {photos.length > 0 && (
                  <div className="mt-3 grid grid-cols-4 gap-2">
                    {photos.map((p, i) => (
                      <div key={i} className="relative aspect-square rounded-lg overflow-hidden bg-gray-100 border border-gray-200">
                        <img src={p} alt="" className="w-full h-full object-cover" />
                        <button onClick={() => setPhotos(photos.filter((_, j) => j !== i))}
                          className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}
          {step === 4 && (
            <>
              {similar.length > 0 && (
                <div className="rounded-2xl bg-amber-50 border border-amber-300 p-5">
                  <div className="text-sm font-bold text-amber-900">⚠️ Similar issue already reported</div>
                  <div className="text-xs text-amber-800 mt-1">An issue like yours has already been reported in your area. Join it instead of posting a duplicate.</div>
                  <div className="mt-3 space-y-2">
                    {similar.map((s) => {
                      const meta = CATEGORY_META[s.category] || CATEGORY_META.other;
                      const sm = STATUS_META[s.status] || STATUS_META.reported;
                      return (
                        <div key={s.id} className="rounded-xl bg-white border border-amber-200 p-3">
                          <div className="flex items-start gap-3">
                            <div className="text-2xl">{meta.icon}</div>
                            <div className="flex-1">
                              <div className="font-semibold text-gray-900 text-sm">{s.title}</div>
                              <div className="text-xs text-gray-500 mt-0.5">📍 {s.village} · 👥 {s.affected_count || 1} affected · <span className={`px-2 py-0.5 rounded-full border text-[10px] ${sm.color}`}>{sm.label}</span></div>
                              <div className="mt-2 flex gap-2 flex-wrap">
                                <button data-testid={`join-${s.id}`} onClick={() => joinExisting(s.id)}
                                  className="px-3 py-1.5 rounded-full bg-emerald-600 text-white text-xs font-semibold">I'm also affected</button>
                                <button onClick={() => nav(`/community/${s.id}`)}
                                  className="px-3 py-1.5 rounded-full bg-white border border-gray-200 text-xs font-semibold">View issue</button>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div className="mt-3 text-xs text-amber-900">If your problem is genuinely different, continue below and submit.</div>
                </div>
              )}
              <div className={similar.length ? "mt-6" : ""}>
                <div className="text-xs uppercase tracking-widest text-gray-500 font-semibold">Review</div>
                <div className="mt-2 rounded-2xl bg-gray-50 border border-gray-200 p-5 text-sm space-y-2">
                  <div><b>Title:</b> {form.title}</div>
                  <div><b>Category:</b> {CATEGORY_META[form.category]?.label}</div>
                  <div><b>Location:</b> {[form.village, form.district, form.state].filter(Boolean).join(", ")}</div>
                  <div className="whitespace-pre-line"><b>Description:</b> {form.description}</div>
                  {photos.length > 0 && <div><b>Photos:</b> {photos.length}</div>}
                </div>
              </div>
            </>
          )}
        </div>

        <div className="mt-6 flex gap-3 justify-end">
          {step > 1 && <button onClick={() => setStep(step - 1)} className="px-5 py-3 rounded-full border border-gray-200">Back</button>}
          {step < 4 && (
            <button data-testid="issue-next" onClick={next} disabled={busy}
              className={`px-6 py-3 rounded-full ${theme.bg} text-white font-semibold`}>
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Continue"}
            </button>
          )}
          {step === 4 && (
            <button data-testid="issue-submit" onClick={submit} disabled={busy}
              className={`px-6 py-3 rounded-full ${theme.bg} text-white font-semibold`}>
              {busy ? "Submitting…" : "Submit Issue"}
            </button>
          )}
        </div>
      </div>
    </AppShell>
  );
}
